/**
 * LoginPage - Authentication with Core Carousel Identity
 *
 * Features:
 * - Animated Core logo
 * - Email + password sign in
 * - Password recovery flow
 * - Dark mode by default (Core Carousel standard)
 * - Gradient background with purple primary accents
 * - Error handling with visual feedback
 *
 * Registration has been moved to /signup (SignupPage.tsx).
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import CoreStudioLogo from '@/components/ui/core-studio-logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail, Lock, LogIn, ArrowLeft, Send } from 'lucide-react';

type AuthMode = 'login' | 'forgot';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();
  const { signIn, resetPassword, isConfigured } = useAuth();

  // Where to redirect after successful login (from ProtectedRoute)
  const redirectTo = searchParams.get('redirect') || '/';

  const [mode, setMode] = useState<AuthMode>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Ensure dark mode is applied on login page
  useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!email.trim()) {
      setError(t('login.fillEmail'));
      return;
    }

    if (mode === 'login') {
      if (!password.trim()) {
        setError(t('login.fillRequired'));
        return;
      }

      if (password.length < 6) {
        setError(t('login.passwordMinLength'));
        return;
      }
    }

    setLoading(true);

    try {
      if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) {
          setError(error.message.startsWith('auth.') ? t(error.message) : error.message);
        } else {
          setSuccess(t('login.successRecovery'));
        }
      } else {
        const { error } = await signIn(email, password);
        if (error) {
          setError(error.message.startsWith('auth.') ? t(error.message) : error.message);
        } else {
          navigate(redirectTo, { replace: true });
        }
      }
    } catch (err) {
      setError(t('login.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  // Dev mode: skip auth if backend not configured
  if (!isConfigured) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <CoreStudioLogo width={200} useCurrentColor className="text-foreground mb-8" />
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle className="text-xl">{t('login.devMode')}</CardTitle>
            <CardDescription>{t('login.devDescription')}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/')} className="w-full">
              {t('login.devContinue')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4 scanlines" style={{ background: 'linear-gradient(62.4deg, #4763CA 0%, #030513 33.9%, #06091B 63.4%, #4763CA 100%)' }}>
      {/* Language switcher - top right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-md space-y-8">
        {/* Logo */}
        <div className="flex justify-center">
          <CoreStudioLogo width={200} className="text-foreground" />
        </div>

        {/* Auth Card */}
        <Card className="border-border/50 shadow-xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl font-bold">
              {mode === 'login' ? t('login.title') : t('login.titleForgot')}
            </CardTitle>
            <CardDescription>
              {mode === 'login' ? t('login.subtitle') : t('login.subtitleForgot')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email */}
              <div>
                <Label htmlFor="email" className="text-sm font-medium">
                  {t('login.email')}
                </Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('login.placeholderEmail')}
                    className="pl-10"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              {/* Password (hidden in forgot mode) */}
              {mode === 'login' && (
                <div>
                  <Label htmlFor="password" className="text-sm font-medium">
                    {t('login.password')}
                  </Label>
                  <div className="relative mt-1">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder={t('login.placeholderPassword')}
                      className="pl-10"
                      disabled={loading}
                      required
                      minLength={6}
                    />
                  </div>
                </div>
              )}

              {/* Forgot password link (login mode only) */}
              {mode === 'login' && (
                <div className="text-right">
                  <button
                    type="button"
                    onClick={() => {
                      setMode('forgot');
                      setError(null);
                      setSuccess(null);
                    }}
                    className="text-xs text-muted-foreground hover:text-primary transition-colors"
                    disabled={loading}
                  >
                    {t('login.forgotPassword')}
                  </button>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* Success Message */}
              {success && (
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3">
                  <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    {mode === 'login' ? t('login.entering') : t('login.sending')}
                  </>
                ) : (
                  <>
                    {mode === 'login' ? (
                      <>
                        <LogIn className="h-4 w-4 mr-2" />
                        {t('login.submit')}
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        {t('login.submitForgot')}
                      </>
                    )}
                  </>
                )}
              </Button>
            </form>

            {/* Toggle Mode */}
            <div className="mt-6 text-center space-y-2">
              {mode === 'forgot' ? (
                <button
                  type="button"
                  onClick={() => {
                    setMode('login');
                    setError(null);
                    setSuccess(null);
                  }}
                  className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
                  disabled={loading}
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {t('login.backToLogin')}
                </button>
              ) : (
                <Link
                  to="/signup"
                  className="text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  {t('login.noAccount')}
                </Link>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          {t('common.copyright', { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
